import type {
  ListingRulesConfig,
  LRGroup as ListingRuleGroup,
  LRRule as ListingRule,
} from '@/types/appTypes'

// Re-export types for convenience
export type { ListingRulesConfig, ListingRuleGroup, ListingRule }

/**
 * Normalize/clean publish rules prior to saving.
 * - Removes nested groups inside groups (unsupported)
 * - Removes empty/invalid rules
 * - Ensures evaluation and ruleset defaults
 * - Keeps standalone VIN rules if present (backward compatibility)
 */
export function cleanupListingRules(config: ListingRulesConfig): ListingRulesConfig {
  if (!config || typeof config !== 'object') return config

  const result: ListingRulesConfig = JSON.parse(JSON.stringify(config))

  for (const segmentId in result) {
    const segment = result[segmentId] || (result[segmentId] = {})
    const lr = (segment.inventory_membership_rules = segment.inventory_membership_rules || {})
    const ruleset = (lr.ruleset = lr.ruleset || {
      logic: 'AND',
      rules: [] as Array<ListingRule | ListingRuleGroup>,
    })
    lr.evaluation = lr.evaluation || { mode: 'first_match', fallback_action: 'allow' }
    ruleset.logic = (ruleset.logic as any) || 'AND'
    ruleset.rules = Array.isArray(ruleset.rules) ? ruleset.rules : []

    ruleset.rules = ruleset.rules
      .map((item: any) => {
        // Standalone rule
        if (item?.type === 'rule') {
          if (!isValidRule(item)) return null
          return item
        }
        // Group
        if (item?.type === 'group' && Array.isArray(item.rules)) {
          const cleanedRules = item.rules.filter((r: any) => r?.type !== 'group' && isValidRule(r))
          if (cleanedRules.length === 0) return null
          // IMPORTANT:
          // Preserve stable `id` fields (rule + group) so patch-based drafts can diff by ID.
          // If we drop group IDs here, the backend interprets the unchanged groups as
          // "remove old group" + "add new group", creating noisy ConfigDraft ops.
          return {
            ...item,
            type: 'group',
            logic: item.logic === 'AND' || item.logic === 'OR' ? item.logic : 'OR',
            rules: cleanedRules,
            action: item.action || 'block',
            reason: item.reason || '',
          } as ListingRuleGroup
        }
        return null
      })
      .filter(Boolean) as Array<ListingRule | ListingRuleGroup>
  }

  return result
}

/**
 * Feed Source Configuration owns feed-routing rules generated from the
 * feed-per-state matrix. Publish Rules should never expose those rules as
 * editable dealer-authored rules, but the underlying config must keep them so
 * publish evaluation remains unchanged.
 */
export function isFeedRoutingGeneratedRule(item: unknown): boolean {
  const rule = item as Record<string, any> | null | undefined
  if (!rule || typeof rule !== 'object') return false

  // Strongest/official marker written by the feed mapping backend.
  if (rule._feedMappingGenerated === true) return true

  // Hidden system-managed archive VIN block (archiveBlockRule.ts on the backend). It is a lifecycle
  // side effect of archiving a feed-backed vehicle, never a dealer-authored Publish Rule, so it must
  // be hidden from the Publish Rules UI/diffs the same way feed-routing rules are.
  if (rule._manualArchiveManaged === true) return true
  if (String(rule.reason || '').trim() === 'VIN_MANUALLY_ARCHIVED') return true

  // Conservative metadata fallbacks for future schema variants.
  const markerCandidates = [
    rule.generatedBy,
    rule.source,
    rule.sourceType,
    rule.ruleSource,
    rule.metadata?.generatedBy,
    rule.metadata?.source,
    rule.metadata?.sourceType,
    rule.provenance?.generatedBy,
    rule.provenance?.source,
    rule.provenance?.sourceType,
  ]

  const generatedByFeedRoutingMetadata = markerCandidates.some((value) => {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
    return [
      'feedmapping',
      'feed_mapping',
      'feed-mapping',
      'feedrouting',
      'feed_routing',
      'feed-routing',
      'feedsourceconfig',
      'feed_source_config',
      'feed-source-config',
      'feedsourceconfiguration',
      'feed_source_configuration',
      'feed-source-configuration',
    ].includes(normalized)
  })
  if (generatedByFeedRoutingMetadata) return true

  // Legacy generated rules from Feed Source Configuration may not carry the
  // marker on older/onboarding configs. Their actual enforcement condition is
  // source-authority via `available_feeds` (typically `not_contains <feed>`).
  // Hide these from Publish Rules, but do not remove them from the config.
  if (containsAvailableFeedsCondition(rule)) return true

  // Legacy/fallback marker from generated rule reasons. Keep this intentionally
  // narrow so normal dealer-authored reasons containing "feed" are not hidden.
  return isGeneratedFeedRoutingReason(rule.reason)
}

function containsAvailableFeedsCondition(item: unknown): boolean {
  const rule = item as Record<string, any> | null | undefined
  if (!rule || typeof rule !== 'object') return false

  if (String(rule.field || '').trim().toLowerCase() === 'available_feeds') {
    return true
  }

  if (Array.isArray(rule.rules)) {
    return rule.rules.some(containsAvailableFeedsCondition)
  }

  return false
}

function isGeneratedFeedRoutingReason(reason: unknown): boolean {
  const normalized = String(reason || '').trim()
  if (!normalized) return false

  if (normalized.toLowerCase().startsWith('[auto:feed-mapping]')) return true

  // Older generated Feed Source Configuration rules were saved with operator-ish
  // reasons like "ON-LOT NOT IN VAUTO" or "NEW EV NOT IN FDDS2" and no metadata
  // marker. Match those generated source-authority labels without hiding normal
  // dealer-authored prose that merely mentions feeds.
  return /\b(?:on[-\s]?lot|in[-\s]?transit|fctp|new|used|cpo|certified|ev|non[-\s]?ev)\b.*\bnot\s+in\b\s+[a-z0-9_-]+\b/i.test(
    normalized,
  )
}

/**
 * Returns true if publish rules include a VIN-specific block rule for the provided VIN.
 * This is used to represent a manually hidden-from-site vehicle.
 */
export function hasVinVisibilityBlockRule(config: ListingRulesConfig | null | undefined, vin: string) {
  if (!config || !vin) return false
  const needle = String(vin).toUpperCase()
  const rules: any[] = (config as any)?.all?.inventory_membership_rules?.ruleset?.rules || []
  if (!Array.isArray(rules)) return false

  return rules.some((item) => {
    // Group with VIN rule
    if (item?.type === 'group' && Array.isArray(item.rules)) {
      const isBlock = String(item.action || 'block').toLowerCase() === 'block'
      if (!isBlock) return false
      return item.rules.some(
        (r: any) =>
          r?.type === 'rule' &&
          String(r.field).toLowerCase() === 'vin' &&
          ['equals', 'equals_ci'].includes(String(r.operator).toLowerCase()) &&
          String(r.value).toUpperCase() === needle,
      )
    }

    // Standalone rule (legacy/back-compat)
    const isBlock = String(item?.action || 'block').toLowerCase() === 'block'
    return (
      item?.type === 'rule' &&
      isBlock &&
      String(item.field).toLowerCase() === 'vin' &&
      ['equals', 'equals_ci'].includes(String(item.operator).toLowerCase()) &&
      String(item.value).toUpperCase() === needle
    )
  })
}

/**
 * Adds a VIN-specific block rule (as a group) for the provided VIN.
 * Returns a new config object.
 */
export function addVinVisibilityBlockRule(config: ListingRulesConfig, vin: string): ListingRulesConfig {
  const next: ListingRulesConfig = JSON.parse(JSON.stringify(config || {}))
  const needle = String(vin || '').trim()
  if (!needle) return next

  next.all = (next as any).all || {
    inventory_membership_rules: {
      ruleset: { logic: 'AND', rules: [] },
      evaluation: { mode: 'first_match', fallback_action: 'allow' },
    },
  }

  const listingRules = ((next as any).all.inventory_membership_rules = (next as any).all.inventory_membership_rules || {})
  const ruleset = (listingRules.ruleset = listingRules.ruleset || { logic: 'AND', rules: [] })
  ruleset.rules = Array.isArray(ruleset.rules) ? ruleset.rules : []

  if (!hasVinVisibilityBlockRule(next, needle)) {
    ruleset.rules.push({
      type: 'group',
      logic: 'OR',
      rules: [
        {
          type: 'rule',
          field: 'vin',
          operator: 'equals_ci',
          value: needle,
        },
      ],
      action: 'block',
      reason: 'VIN_MANUALLY_BLOCKED',
    })
  }

  return next
}

/**
 * Removes VIN-specific block rules for the provided VIN.
 * - Removes standalone VIN rules (legacy/back-compat)
 * - Removes matching VIN rules inside groups; drops groups that become empty
 *
 * Returns a new config object and whether anything was removed.
 */
export function removeVinVisibilityBlockRule(
  config: ListingRulesConfig,
  vin: string,
): { config: ListingRulesConfig; removed: boolean } {
  const next: ListingRulesConfig = JSON.parse(JSON.stringify(config || {}))
  const needle = String(vin || '').toUpperCase()
  if (!needle) return { config: next, removed: false }

  const rules: any[] = (next as any)?.all?.inventory_membership_rules?.ruleset?.rules
  if (!Array.isArray(rules)) return { config: next, removed: false }

  let removed = false
  const updated = rules
    .map((item) => {
      // Group: remove matching VIN rule(s)
      if (item?.type === 'group' && Array.isArray(item.rules)) {
        const isBlock = String(item.action || 'block').toLowerCase() === 'block'
        if (!isBlock) return item

        const beforeLen = item.rules.length
        const newRules = item.rules.filter((r: any) => {
          if (r?.type !== 'rule') return true
          if (String(r.field).toLowerCase() !== 'vin') return true
          if (!['equals', 'equals_ci'].includes(String(r.operator).toLowerCase())) return true
          const matches = String(r.value).toUpperCase() === needle
          return !matches
        })

        if (newRules.length !== beforeLen) removed = true
        if (newRules.length === 0) return null
        return { ...item, rules: newRules }
      }

      // Standalone rule (legacy/back-compat)
      if (item?.type === 'rule') {
        const isBlock = String(item.action || 'block').toLowerCase() === 'block'
        const matches =
          isBlock &&
          String(item.field).toLowerCase() === 'vin' &&
          ['equals', 'equals_ci'].includes(String(item.operator).toLowerCase()) &&
          String(item.value).toUpperCase() === needle
        if (matches) {
          removed = true
          return null
        }
      }

      return item
    })
    .filter(Boolean)

  ;(next as any).all = (next as any).all || {}
  ;(next as any).all.inventory_membership_rules = (next as any).all.inventory_membership_rules || {}
  ;(next as any).all.inventory_membership_rules.ruleset = (next as any).all.inventory_membership_rules.ruleset || {
    logic: 'AND',
    rules: [],
  }
  ;(next as any).all.inventory_membership_rules.ruleset.rules = updated

  return { config: next, removed }
}

/** @deprecated Use hasVinVisibilityBlockRule. */
export const hasVinDelistBlockRule = hasVinVisibilityBlockRule

/** @deprecated Use addVinVisibilityBlockRule. */
export const addVinDelistBlockRule = addVinVisibilityBlockRule

/** @deprecated Use removeVinVisibilityBlockRule. */
export const removeVinDelistBlockRule = removeVinVisibilityBlockRule

function isValidRule(rule: any): rule is ListingRule {
  if (!rule || rule.type !== 'rule') return false
  if (!rule.field || String(rule.field).trim() === '') return false
  const operatorsRequiringValue = [
    'equals',
    'equals_ci',
    'not_equals',
    'contains',
    'not_contains',
    'gt',
    'lt',
    'length_not_in',
  ]
  if (operatorsRequiringValue.includes(String(rule.operator))) {
    if (rule.value === null || rule.value === undefined || rule.value === '') return false
    if (Array.isArray(rule.value) && rule.value.length === 0) return false
  }
  return true
}
